/**
 * React hook for environment detection and environment-specific functionality
 */

import { useState, useEffect, useMemo } from "react";
import {
  getEnvironment,
  getEnvironmentConfig,
  isFeatureAvailable,
  type Environment,
  type EnvironmentInfo,
} from "../utils/environment";

/**
 * Hook return type with environment information and utilities
 */
export interface UseEnvironmentReturn {
  /** The detected environment type */
  environment: Environment;
  /** Whether running in Electron */
  isElectron: boolean;
  /** Whether running in a web browser */
  isWeb: boolean;
  /** Complete environment information */
  environmentInfo: EnvironmentInfo;
  /** Environment-specific configuration */
  config: ReturnType<typeof getEnvironmentConfig>;
  /** Check if a specific feature is available */
  isFeatureAvailable: (feature: "ipc" | "shell" | "fileSystem" | "nativeMenus") => boolean;
  /** Whether running in development mode */
  isDevelopment: boolean;
  /** Whether running in production mode */
  isProduction: boolean;
}

/**
 * React hook for environment detection
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isElectron, isWeb, isFeatureAvailable } = useEnvironment();
 *
 *   return (
 *     <div>
 *       {isElectron && <ElectronSpecificFeature />}
 *       {isWeb && <WebSpecificFeature />}
 *       {isFeatureAvailable('ipc') && <IPCFeature />}
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns Environment information and utilities
 */
export function useEnvironment(): UseEnvironmentReturn {
  const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo>(() => getEnvironment());

  // Poll for environment changes (e.g. Electron APIs becoming available), but
  // bail out of re-rendering when nothing actually changed. Returning the
  // previous object keeps React from re-rendering every tick — without this
  // guard the fresh object from getEnvironment() forced a render every second
  // across every consumer (and re-ran getEnvVar in a tight, tab-freezing loop).
  useEffect(() => {
    const updateEnvironment = () => {
      setEnvironmentInfo((prev) => {
        const next = getEnvironment();
        const unchanged =
          prev.environment === next.environment &&
          prev.isElectron === next.isElectron &&
          prev.isWeb === next.isWeb &&
          prev.isDevelopment === next.isDevelopment &&
          prev.isProduction === next.isProduction;
        return unchanged ? prev : next;
      });
    };

    updateEnvironment();
    const interval = setInterval(updateEnvironment, 1000);
    return () => clearInterval(interval);
  }, []);

  // Memoize configuration to avoid recalculation
  const config = useMemo(() => getEnvironmentConfig(), [environmentInfo]);

  // Memoize feature availability checker
  const checkFeatureAvailability = useMemo(
    () => (feature: "ipc" | "shell" | "fileSystem" | "nativeMenus") => isFeatureAvailable(feature),
    [environmentInfo],
  );

  return {
    environment: environmentInfo.environment,
    isElectron: environmentInfo.isElectron,
    isWeb: environmentInfo.isWeb,
    environmentInfo,
    config,
    isFeatureAvailable: checkFeatureAvailability,
    isDevelopment: environmentInfo.isDevelopment,
    isProduction: environmentInfo.isProduction,
  };
}

/**
 * Hook for conditional rendering based on environment
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { showFor } = useEnvironmentConditional();
 *
 *   return (
 *     <div>
 *       {showFor.electron(<ElectronFeature />)}
 *       {showFor.web(<WebFeature />)}
 *       {showFor.development(<DevTools />)}
 *     </div>
 *   );
 * }
 * ```
 */
export function useEnvironmentConditional() {
  const { isElectron, isWeb, isDevelopment, isProduction } = useEnvironment();

  return {
    /**
     * Show component only in Electron environment
     */
    showFor: {
      electron: <T,>(component: T): T | null => (isElectron ? component : null),
      web: <T,>(component: T): T | null => (isWeb ? component : null),
      development: <T,>(component: T): T | null => (isDevelopment ? component : null),
      production: <T,>(component: T): T | null => (isProduction ? component : null),
    },

    /**
     * Show component only if feature is available
     */
    showIfFeature:
      (feature: "ipc" | "shell" | "fileSystem" | "nativeMenus") =>
      <T,>(component: T): T | null =>
        isFeatureAvailable(feature) ? component : null,
  };
}

/**
 * Hook for environment-specific styling
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { getEnvironmentClasses } = useEnvironmentStyling();
 *
 *   return (
 *     <div className={getEnvironmentClasses({
 *       base: 'p-4',
 *       electron: 'border-2',
 *       web: 'shadow-lg',
 *       development: 'bg-status-warning-soft'
 *     })}>
 *       Content
 *     </div>
 *   );
 * }
 * ```
 */
export function useEnvironmentStyling() {
  const { isElectron, isWeb, isDevelopment, isProduction } = useEnvironment();

  return {
    /**
     * Get CSS classes based on environment
     */
    getEnvironmentClasses: (classes: {
      base?: string;
      electron?: string;
      web?: string;
      development?: string;
      production?: string;
    }): string => {
      const { base = "", electron = "", web = "", development = "", production = "" } = classes;

      let result = base;

      if (isElectron) result += ` ${electron}`;
      if (isWeb) result += ` ${web}`;
      if (isDevelopment) result += ` ${development}`;
      if (isProduction) result += ` ${production}`;

      return result.trim();
    },
  };
}
